import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import PatientDetails from "./PatientDetails";
import PreviousVisit from "./PreviousVisit";
import Planning from "./Planning";
import PatientCondition from "./PatientCondition";
import ApartmentCondition from "./ApartmentCondition";
import Satisfaction from "./Satisfaction";
import General from "./General";
import { Emergency } from "../../../components/Emergency";
import Summary from "./Summary";
import { useSelector, useDispatch } from "react-redux";
import {
  getFullName,
  getAddress,
  getChangedItems,
  getLocalPhoneNumber,
} from "../../../components/util/helpers";
import {
  setChangedFormItems,
  commitChanges,
  createRecord,
  loadRecord,
} from "../visitSlice";
import moment from "moment";
import { IonButton, IonLoading } from "@ionic/react";
import { ErrorMessage } from "@hookform/error-message";

const FormWrapper = (props) => {
  const { present, type, hideShowPatientBtn, patientId, branchId } = props;
  const [name, setName] = useState("");
  const [newFormStatus, setNewFormStatus] = useState("inprogress");
  const [employees, setEmployees] = useState([]);
  const [plannedIsController, setPlannedIsController] = useState(null);
  const [userIsProOrAdmin, setUserIsProOrAdmin] = useState(null);
  const activeHouseVisit = useSelector((state) => state.visit.activeHouseVisit);
  const hasEmergencyDetails = useSelector(state => !!state.visit.activeHouseVisit.patient__emergency__has_emergency_details);
  const intl = useIntl();
  const dispatch = useDispatch();
  const saveLoading = useSelector(
    (state) => state.pendingActions["visit/update"]
  );
  const createLoading = useSelector(
    (state) => state.pendingActions["visit/create"]
  );

  const loadLoading = useSelector(
    (state) => state.pendingActions["visit/load"]
  );
  const profile = useSelector((state) => state.auth.profile);
  const changedItems = useSelector((state) => state.visit.changedItems.changed);
  const changedValues = useSelector((state) => state.visit.changedItems.values);
  const { approved } = activeHouseVisit;

  const { handleSubmit, control, formState: { errors }, watch, reset } = useForm({});

  useEffect(() => {
    const employeeId =
      changedValues.planned_employee_id || activeHouseVisit.planned_employee_id;
    const filteredFormEmployee = employees.filter(
      (i) => i.id === employeeId
    )[0];
    if (filteredFormEmployee) {
      setPlannedIsController(filteredFormEmployee.professional_role_id === 3);
    }
    const filteredUserEmployee =
      profile.associated_employee_id &&
      employees.filter((i) => i.id === profile.associated_employee_id)[0];
    const isPro =
      filteredUserEmployee &&
      filteredUserEmployee.professional_role_id &&
      filteredUserEmployee.professional_role_id !== 3;
    if (isPro || profile.admin) {
      setUserIsProOrAdmin(true);
    }
  }, [employees, activeHouseVisit, changedValues, profile]);

  useEffect(() => {
    const defaultValues = {
      ...activeHouseVisit,
      patient__local_id_number:
        activeHouseVisit && activeHouseVisit.patient__local_id_number,
      mobile_number:
        activeHouseVisit &&
        getLocalPhoneNumber(
          activeHouseVisit.patient__contact_info__mobile_number
        ),
      phone_number:
        activeHouseVisit &&
        getLocalPhoneNumber(
          activeHouseVisit.patient__contact_info__phone_number
        ),
      patient_address:
        activeHouseVisit &&
        getAddress({
          "address.street": activeHouseVisit.patient__address__street,
          "address.house_number":
            activeHouseVisit.patient__address__house_number,
          "address.city": activeHouseVisit.patient__address__city,
        }),
      house_visit_comments:
        activeHouseVisit && activeHouseVisit.patient__house_visit_comments,

      previous_visit__date:
        activeHouseVisit &&
        moment(activeHouseVisit.previous_visit__date).format("DD/MM/YYYY"),
      previous_visit__duration_minutes:
        activeHouseVisit && activeHouseVisit.previous_visit__duration_minutes
          ? `${activeHouseVisit.previous_visit__duration_minutes
          } ${intl.formatMessage({
            id: "app.mobile.forms.house_visits.minutes",
          })}`
          : `-`,
      previous_visit__planned_employee_id: name,
      previous_visit__care_giver_was_presented: activeHouseVisit.previous_visit__care_giver_was_presented
        ? intl.formatMessage({
          id: "app.mobile.dataSelect.yes",
        })
        : intl.formatMessage({
          id: "app.mobile.dataSelect.no",
        }),
    };
    reset(defaultValues);
  }, [activeHouseVisit, intl, name, reset]);

  useEffect(() => {
    if (activeHouseVisit.planned_employee__surename) {
      let employeeName = getFullName(
        activeHouseVisit.planned_employee__surename,
        activeHouseVisit.planned_employee__first_name
      );
      if (activeHouseVisit.planned_employee__professional_role_id) {
        const role =
          activeHouseVisit.planned_employee__professional_role_id &&
          intl.formatMessage({
            id: `app.mobile.dataSelect.professional_role_id.${activeHouseVisit.planned_employee__professional_role_id}`,
            defaultMessage: "",
          });
        employeeName = employeeName.concat(", ", role);
      }
      setName(employeeName);
    }
  }, [
    intl,
    activeHouseVisit.planned_employee__surename,
    activeHouseVisit.planned_employee__first_name,
    activeHouseVisit.planned_employee__professional_role_id,
  ]);

  useEffect(() => {
    const readOnlyFields = [
      "patient_name",
      "patient__local_id_number",
      "phone_number",
      "patient_address",
      "previous_visit__date",
      "previous_visit__duration_minutes",
      "previous_visit__planned_employee_id",
      "previous_visit__care_giver_was_presented",
    ];
    const subscription = watch((value, { name, type }) => {
      const isReadOnly = readOnlyFields.includes(name);
      if (activeHouseVisit.id && !isReadOnly) {
        const arr = getChangedItems(
          { [name]: value[name] },
          value,
          changedItems,
          activeHouseVisit
        );
        dispatch(setChangedFormItems({ changed: arr, values: value }));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, changedItems, activeHouseVisit, dispatch]);

  const onFinish = async (values) => {
    const action = await dispatch(
      type === "new" && newFormStatus === "inprogress"
        ? createRecord()
        : commitChanges()
    );
    if (type === "new") {
      setNewFormStatus("done");
    }
    if (!action.error) {
      const params = {
        patientId: values.patient__id,
        branchId: values.patient__branch,
        admin: false,
        houseVisitsId: action.payload.data.id,
      };
      dispatch(loadRecord(params));
    }
  };

  const setProffesionalEmployees = (val) => {
    if (employees.length === 0 && val.length > 0) {
      setEmployees(val);
    }
  };

  const date = changedItems.includes("date")
    ? changedValues["date"]
    : activeHouseVisit["date"];
  const futureVisit =
    date && moment().endOf("day") <= moment(date).startOf("day");
  const enabled = !activeHouseVisit["verified"];

  return (
    <form onSubmit={handleSubmit(onFinish)}>
      <PatientDetails
        control={control}
        present={present}
        loading={loadLoading}
        type={type}
        hideShowPatientBtn={hideShowPatientBtn}
        branchId={branchId}
        patientId={patientId}
        activeHouseVisit={activeHouseVisit}
      />
      {hasEmergencyDetails &&
        <Emergency
          activeHouseVisit={activeHouseVisit}
          approved={approved}
          changedItems={changedItems}
          changedValues={changedValues}
          control={control}
          enabled={enabled}
          isFromVisitScreen={true}
          loading={loadLoading}
        />
      }
      {type !== "new" && (
        <PreviousVisit control={control} loading={loadLoading} />
      )}
      <Planning
        activeHouseVisit={activeHouseVisit}
        control={control}
        approved={approved}
        loading={loadLoading}
        setProffesionalEmployees={setProffesionalEmployees}
        enabled={
          type === "new" && newFormStatus === "inprogress" ? true : enabled
        }
        type={type}
        changedValues={changedValues}
        changedItems={changedItems}
      />
      {!(type === "new" && newFormStatus === "inprogress") && (
        <div>
          <General
            control={control}
            approved={approved}
            loading={loadLoading}
            enabled={enabled}
            activeHouseVisit={activeHouseVisit}
            changedValues={changedValues}
            changedItems={changedItems}
          />
          <PatientCondition
            control={control}
            approved={approved}
            loading={loadLoading}
            enabled={enabled}
          />
          <ApartmentCondition
            control={control}
            approved={approved}
            loading={loadLoading}
            enabled={enabled}
          />
          <Satisfaction
            control={control}
            approved={approved}
            loading={loadLoading}
            enabled={enabled}
          />
          <Summary
            control={control}
            activeHouseVisit={activeHouseVisit}
            changedValues={changedValues}
            changedItems={changedItems}
            approved={approved}
            loading={loadLoading}
            enabled={enabled}
            futureVisit={futureVisit}
            userIsProOrAdmin={userIsProOrAdmin}
          />
        </div>
      )}

      <div className="errors_container">
        <ErrorMessage
          errors={errors}
          name="patient__emergency__guidance_date"
          render={({ message, messages }) => {
            if (message) {
              return (<p>{message}</p>);
            }
            if (messages) {
              return Object.entries(messages).map(([type, message]) => (
                <p key={type}>{message}</p>
              ))
            }
            return <p>An error in the form occured. Check that everything was entered correctly</p>;
          }}
        />
        <ErrorMessage
          errors={errors}
          name="patient__emergency__way_of_communication_id"
          render={({ message, messages }) => {
            if (message) {
              return (<p>{message}</p>);
            }
            if (messages) {
              return Object.entries(messages).map(([type, message]) => (
                <p key={type}>{message}</p>
              ))
            }
            return <p>An error in the form occured. Check that everything was entered correctly</p>;
          }}
        />

      </div>
      <div style={{ padding: "0 16px 16px 16px" }}>
        {(userIsProOrAdmin ||
          enabled ||
          (type === "new" && newFormStatus === "inprogress")) && (
            <IonButton
              expand="block"
              type="submit"
              disabled={!changedItems || changedItems.length === 0}
            >
              {intl.formatMessage({
                id:
                  type === "new" && newFormStatus === "inprogress"
                    ? "app.mobile.actions.create_house_visit"
                    : "app.mobile.actions.save",
              })}
            </IonButton>
          )}

        {(saveLoading || createLoading) && (
          <IonLoading
            isOpen={true}
            message={intl.formatMessage({
              id: "app.mobile.alert.saving_data",
            })}
          />
        )}
      </div>
    </form>
  );
};

export default FormWrapper;
